import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, Lock, Calendar, CheckCircle, 
  AlertCircle, ArrowRight, Eye, EyeOff, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast, Toaster } from 'sonner';

const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const SetupAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [orderInfo, setOrderInfo] = useState(null);
  const [error, setError] = useState(null);
  
  const [step, setStep] = useState(1); // 1: Profile, 2: Verification
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    age: '',
    password: '',
    confirmPassword: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  useEffect(() => {
    if (!token) {
      setError('Invalid setup link. Please check your email for the correct link.');
      setLoading(false);
      return;
    }
    
    fetchOrderInfo();
  }, [token]);
  
  const fetchOrderInfo = async () => {
    try {
      const response = await fetch(`${API}/orders/setup/${token}`);
      const data = await response.json();
      
      if (data.success) {
        setOrderInfo(data.order);
      } else {
        setError(data.message || 'Invalid or expired setup link');
      }
    } catch (err) {
      setError('Failed to load order information');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmitProfile = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.firstName || !formData.lastName) {
      toast.error('Please enter your full name');
      return;
    }
    if (!formData.phone) {
      toast.error('Please enter your phone number');
      return;
    }
    if (!formData.age || parseInt(formData.age) < 18) {
      toast.error('You must be 18 or older to create an account');
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/orders/create-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          age: parseInt(formData.age),
          password: formData.password
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Profile created! Check your email for verification code.');
        setStep(2);
      } else {
        toast.error(data.message || 'Failed to create account');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter the 6-digit verification code');
      return;
    }
    
    setVerifying(true);
    
    try {
      const response = await fetch(`${API}/orders/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: orderInfo.email,
          code: verificationCode
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        localStorage.setItem('guestToken', data.token);
        toast.success('Account verified! Redirecting to your dashboard...');
        setTimeout(() => {
          navigate('/portal');
        }, 1500);
      } else {
        toast.error(data.message || 'Invalid verification code');
      }
    } catch (err) {
      toast.error('Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading your order...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="bg-slate-900 border border-white/10 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Setup Link Error</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-500"
          >
            Return to Home
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Toaster position="top-right" richColors />
      
      <div className="bg-slate-900 border border-white/10 rounded-2xl p-8 max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {step === 1 ? 'Create Your Account' : 'Verify Your Email'}
          </h1>
          <p className="text-gray-400">
            {step === 1 
              ? 'Complete your profile to access your purchase'
              : 'Enter the code sent to your email'
            }
          </p>
        </div>
        
        {/* Order Info */}
        {orderInfo && step === 1 && (
          <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-400 mb-2">Order #{orderInfo.order_id}</p>
            <p className="text-white font-medium">{orderInfo.email}</p>
            <div className="mt-3 pt-3 border-t border-white/10">
              <p className="text-sm text-gray-400 mb-1">Your Purchase:</p>
              {orderInfo.cart_items?.map((item, idx) => (
                <p key={idx} className="text-sm text-white">• {item.name}</p>
              ))}
            </div>
          </div>
        )}
        
        {step === 1 ? (
          // Step 1: Profile Form
          <form onSubmit={handleSubmitProfile} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">First Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="John"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                  placeholder="Doe"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Phone Number *</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                  placeholder="+1 (555) 123-4567"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Age *</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="number"
                  min="18"
                  max="120"
                  value={formData.age}
                  onChange={(e) => setFormData({...formData, age: e.target.value})}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                  placeholder="25"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">You must be 18 or older</p>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Password *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg pl-10 pr-12 py-3 text-white focus:border-blue-500 focus:outline-none"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Confirm Password *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 py-6"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating Account...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </form>
        ) : (
          // Step 2: Email Verification
          <form onSubmit={handleVerifyEmail} className="space-y-6">
            <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 text-center">
              <Mail className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-sm text-gray-300">
                We sent a 6-digit code to <strong className="text-white">{orderInfo?.email}</strong>
              </p>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-2 text-center">Enter Verification Code</label>
              <input
                type="text"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-4 text-white text-center text-2xl tracking-[0.5em] font-mono focus:border-blue-500 focus:outline-none"
                placeholder="000000"
              />
            </div>
            
            <Button
              type="submit"
              disabled={verifying}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 py-6"
            >
              {verifying ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Verifying...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Verify & Access Dashboard
                </span>
              )}
            </Button>
            
            <p className="text-center text-gray-500 text-sm">
              Didn&apos;t receive the code? Check your spam folder.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default SetupAccount;
